import { AbstractMesh, Axis, Matrix, Mesh, Ray, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { ZEDCamera } from '@modules/standard/src/camera/ZEDCamera';
import { World } from 'miniplex';

import { LayerMask } from '../../Constants';
import { Entity } from '../Entity';

import { ItemHoSystem } from './ItemHoSystem';


export class RaycastSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('RaycastSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly scene;

  private readonly octreeArchetype;
  private readonly itemHoSystem;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [ItemHoSystem.INJECTABLE_TOKEN]: itemHoSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [ItemHoSystem.INJECTABLE_TOKEN]: ItemHoSystem;
  }) {
    this.scene = babylonjs.scene;
    this.itemHoSystem = itemHoSystem;

    this.octreeArchetype = world.with('DanjiOctree');
  }

  dispose() {
    this.groundMesh = undefined;
  }

  // 순환 참조 방지를 위해 GroundSystem을 Injection 받지 않고 bind만 함
  private groundMesh: Mesh | undefined;
  public bindGroundMesh(groundMesh: Mesh) {
    this.groundMesh = groundMesh;
  }

  private readonly rayTop = 10_000;
  private readonly rayBottom = -10_000;
  private readonly caclurateRayLength = this.rayTop - this.rayBottom;
  private caclurateVector: Vector3 = new Vector3(0, this.rayTop, 0);
  private caclurateRay: Ray = new Ray(this.caclurateVector, Vector3.DownReadOnly, this.caclurateRayLength);

  private pickWithRay(x: number, z: number, predicate?: (mesh: AbstractMesh) => boolean, fastCheck = false) {
    this.caclurateVector.x = x;
    this.caclurateVector.z = z;
    this.caclurateRay.origin = this.caclurateVector;

    const hit = this.scene.pickWithRay(this.caclurateRay, predicate, fastCheck);
    return hit?.distance ?? 0 > 0 ? hit : null;
  }

  public pickWithRayByGround(x: number, z: number) {
    return this.pickWithRay(x, z, this.rayPredicateForGround, true);
  }

  /**
   * @remarks zigbang-zed/unity/../DanjiFacilityBuilding.cs에서 RaycastToLand
   */
  public pickWithRayByLand = (x: number, z: number, zOrder?: number) => {
    const hitByBuilding = this.pickWithRay(
      x,
      z,
      (mesh) => {
        const isBasementBuilding = mesh.metadata?.allowRooftopDecal ?? false;
        if (!isBasementBuilding) {
          return false;
        }

        return zOrder === undefined || mesh.metadata?.zOrder < zOrder;
      },
      false
    );
    return hitByBuilding || this.pickWithRayByGround(x, z);
  };

  public getLandPoints(boundPts: Vector3[]): Vector3[] {
    const points: Vector3[] = [];

    boundPts.forEach((v) => {
      const hit = this.pickWithRayByLand(v.x, v.z);

      if (hit?.hit) {
        points.push(hit.pickedPoint!);
      }
    });

    return points;
  }

  private rayPredicateForGround = (mesh: AbstractMesh) => {
    return !!this.groundMesh && mesh === this.groundMesh;
  };

  public isOccludedWithRayByOctree(entityId: string, ray: Ray, ignoreOctreeId?: string) {
    let intersectable = false;
    for (let i = 0; i < this.octreeArchetype.entities.length; i++) {
      const entity = this.octreeArchetype.entities[i]!;
      if (entity.entityId === entityId || entity.entityId === ignoreOctreeId) continue;

      const danjiOctree = entity.DanjiOctree;
      const intersectableOctree = danjiOctree.octree.intersectsRay(ray);

      const otherDistance = Vector3.Distance(ray.origin, danjiOctree.center);
      intersectable = intersectableOctree.length > 0 && otherDistance < ray.length;
      if (intersectable) break;
    }

    return !intersectable;
  }

  public getDirectionFromRayByItemHo(hoId: number) {
    const hoEntity = this.itemHoSystem.getItemHoEntity(hoId);
    const hoMesh = hoEntity?.DanjiMeshes.danjiMeshes.at(0)?.meshes.at(0);

    if (!hoEntity || !hoMesh) {
      console.warn(`해당 ${hoId} 가 존재하지 않습니다.`);
      return undefined;
    }

    const itemCenter = hoMesh.getBoundingInfo().boundingBox.center;
    Vector3.TransformCoordinatesToRef(itemCenter, hoMesh.computeWorldMatrix(true), itemCenter);

    const angle = hoEntity.ItemHo.meta.향;
    const rotationMatrix = Matrix.RotationAxis(Axis.Y, (angle * Math.PI) / 180);
    const windowForward = Vector3.TransformCoordinates(Vector3.Forward(), rotationMatrix).normalize();

    let windowPoint = itemCenter;
    const magnitude = hoMesh.getBoundingInfo().boundingSphere.radius;
    const startRayPoint = itemCenter.add(windowForward.multiplyByFloats(magnitude, magnitude, magnitude));
    const rayDir = new Vector3();
    windowForward.scaleToRef(-1, rayDir);

    const ray = new Ray(startRayPoint, rayDir, Number.MAX_VALUE);
    const hit = this.scene.pickWithRay(ray, (mesh) => mesh === hoMesh, true);

    if (hit && hit.pickedPoint) {
      windowPoint = hit.pickedPoint;
    }

    return {
      itemCenter: itemCenter,
      windowPoint: windowPoint,
      windowForward: windowForward,
    };
  }

  public getCameraRaycastHit() {
    const camera = this.scene.activeCamera as ZEDCamera;
    if (!camera) {
      console.error(new Error('Invalid Camera'));
      return;
    }

    const direction = camera.target.subtract(camera.position).normalize();
    const ray = new Ray(camera.position, direction, Number.MAX_VALUE);
    const pick = this.scene.pickWithRay(ray, (mesh) => {
      if (mesh.layerMask === LayerMask.Default) return false;
      if ((mesh.layerMask & LayerMask.Building) === LayerMask.Building) return true;
      if ((mesh.layerMask & LayerMask.Terrain) === LayerMask.Terrain) return true;
      return false;
    }, true); 

    return pick?.pickedPoint;
  }

  readonly operations: SystemOperationDefinition[] = [];
}
